import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    BU_QUALIFICATION_URL_PREFIX,
    COMPLIANCE_REQUIREMENT_URL_PREFIX,
    complianceRequirementDetailUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    ComplianceRequirement,
    ComplianceRequirementWrite,
} from "~/pages/supplier-360/types/Supplier360Types";

export type UpdateComplianceRequirementVariables = {
    id: number;
    /** Only the fields that changed — a partial `PATCH`. */
    patch: Partial<Omit<ComplianceRequirementWrite, 'entity'>>;
};

/**
 * `PATCH supplier_360/compliance_requirement/{id}/` — docs §2.8, the "Edit compliance record" dialog.
 * Also invalidates the BU qualifications, whose `status` the server recomputes (§2.7).
 */
export function useUpdateComplianceRequirementMutation() {
    const queryClient = useQueryClient();

    return useMutation<ComplianceRequirement, DefaultError, UpdateComplianceRequirementVariables>({
        mutationFn: async ({id, patch}) => {
            const {data} = await queryHttp.patch<ComplianceRequirement>(
                complianceRequirementDetailUrl(id),
                patch,
            );
            return data;
        },
        onSuccess: (updated) => {
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string'
                        && (key.startsWith(COMPLIANCE_REQUIREMENT_URL_PREFIX) || key.startsWith(BU_QUALIFICATION_URL_PREFIX));
                },
            });
            notificationStore.show(`${updated.name} updated.`, {
                severity: 'success',
                id: `compliance-requirement-update-success-${updated.id}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {id}) => {
            notificationStore.show('Could not update the compliance record. Please try again.', {
                severity: 'error',
                id: `compliance-requirement-update-error-${id}`,
                autoHideDuration: 4000,
            });
        },
    });
}
